import type { Module } from 'vuex'
import type { RootState } from '~/store/index'
import type { components } from '~/generated/types.d.ts'
import type { AxiosResponse } from 'axios'

import axios from 'axios'
import useLogger from '~/composables/useLogger'

export interface State {
  // Maps a playlist uuid to the uuid of the current user's favorite record for it
  playlists: Record<string, string>
  count: number
}

const logger = useLogger()

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    playlists: {},
    count: 0
  },
  mutations: {
    playlist: (state, { id, favoriteId, value }: { id: string, favoriteId?: string, value: boolean }) => {
      if (value) {
        state.playlists[id] = favoriteId as string
      } else {
        delete state.playlists[id]
      }

      state.count = Object.keys(state.playlists).length
    },
    reset (state) {
      state.playlists = {}
      state.count = 0
    }
  },
  getters: {
    isFavorite: (state) => (id: string) => {
      return id in state.playlists
    }
  },
  actions: {
    async add ({ commit }, id: string) {
      try {
        const body: components['schemas']['UserPlaylistFavoriteWriteRequest'] = { playlist: id }
        const response = await axios.post<components['schemas']['UserPlaylistFavoriteWrite']>('favorites/playlists/', body)
        commit('playlist', { id, favoriteId: response.data.uuid, value: true })
        logger.info('Successfully added playlist to favorites')
      } catch {
        logger.error('Error while adding playlist to favorites')
      }
    },
    async remove ({ commit, state }, id: string) {
      const favoriteId = state.playlists[id]
      commit('playlist', { id, value: false })

      try {
        await axios.delete(`favorites/playlists/${favoriteId}/`)
        logger.info('Successfully removed playlist from favorites')
      } catch {
        logger.error('Error while removing playlist from favorites')
        commit('playlist', { id, favoriteId, value: true })
      }
    },
    toggle ({ getters, dispatch }, id: string) {
      return getters.isFavorite(id)
        ? dispatch('remove', id)
        : dispatch('add', id)
    },
    async fetch ({ commit }) {
      // Populate the local cache with all playlists the current user has liked,
      // so the favorite state can be checked locally on the playlist detail page.
      // The list endpoint only exposes fid (federation URL) for the favorite and
      // its playlist, not their local uuids, but we need uuids for the DELETE api endpoint, so we extract the trailing uuid
      // segment from each fid here.
      let url: string | null = 'favorites/playlists/'
      const params = { scope: 'me', page_size: 50 }

      while (url) {
        const response: AxiosResponse<components['schemas']['PaginatedPlaylistFavoriteList']> = await axios.get(url, { params })

        for (const result of response.data.results) {
          commit('playlist', {
            id: result.playlist.fid.split('/').filter(Boolean).pop(),
            favoriteId: result.fid.split('/').filter(Boolean).pop(),
            value: true
          })
        }

        url = response.data.next ?? null
      }
    }
  }
}

export default store
